// Normalising any bank question into ONE tap-to-answer shape, for Endurance
// and the Daily — both of which draw from every mode and need a question that
// can be answered with one tap, because by three minutes into an Endurance run
// there are barely four seconds on the clock.
//
// SHUFFLING HERE IS NOT COSMETIC. IT IS THE FAIRNESS OF THE MODE.
//
// The bank stores the correct answer FIRST, because a file where the answer is
// always `answer: 0` is far easier to read and to check for mistakes. Every
// mode SCREEN shuffles before rendering and compares by text, so that never
// leaked — but the quickfire path did not. The result was that in Endurance and
// the Daily, every Guess the Thing question, every Chain Reaction link, and
// (because the bigger thing is nearly always written as `a`) almost every
// Higher or Lower question had its answer on the first button.
//
// Invisible in a screenshot, and fatal to the headline mode: the score is a
// TIME, and a player who notices the top button is usually right ends up with a
// personal best that means nothing.
//
// TRUE/FALSE is deliberately NOT shuffled. It is a fixed pair with a fixed
// meaning; moving the two between questions would punish muscle memory rather
// than reward knowledge.
//
// This lives beside engine.js rather than inside it so the dependency runs one
// way only: quickfire needs the engine's rules, the engine needs nothing from
// here.

import { shuffle, isAusAnswer, drawQuestions } from './engine.js';

/** Reorder options and carry the correct index along with them. */
function scramble(options, answer) {
  const order = shuffle(options.map((_, i) => i));
  return { options: order.map((i) => options[i]), answer: order.indexOf(answer) };
}

const clueFor = (q) => q.clues?.[2] ?? q.clues?.[1] ?? q.clues?.[0] ?? q.prompt;

/** Any bank question as a one-tap question, or null if it cannot be one. */
export function toQuickfire(q) {
  if (!q) return null;
  const base = { id: q.id, topic: q.topic, difficulty: q.difficulty, reason: q.reason, source: q.mode };

  if (q.mode === 'last-gasp') {
    return {
      ...base,
      kind: 'binary',
      prompt: q.prompt,
      options: ['TRUE', 'FALSE'],
      answer: q.answer === true ? 0 : 1,
      aus: isAusAnswer(q, q.prompt),
    };
  }

  if (q.mode === 'higher-lower') {
    const aWins = q.a.value >= q.b.value;
    const win = aWins ? q.a : q.b;
    const s = scramble([`${q.a.emoji} ${q.a.label}`, `${q.b.emoji} ${q.b.label}`], aWins ? 0 : 1);
    return { ...base, kind: 'choice', prompt: q.question, ...s, aus: isAusAnswer(q, win.label) };
  }

  if (q.mode === 'odd-one-out') {
    const s = scramble([...q.items], q.answer);
    return { ...base, kind: 'choice', prompt: q.prompt, ...s, aus: isAusAnswer(q, q.items[q.answer]) };
  }

  if (q.mode === 'guess-the-thing') {
    const s = scramble([...q.options], q.answer);
    return {
      ...base,
      kind: 'choice',
      prompt: clueFor(q),
      glyph: q.glyph,
      ...s,
      aus: isAusAnswer(q, q.options[q.answer]),
    };
  }

  if (q.mode === 'chain-reaction') {
    // Each link stands alone as a question — which is how a mode built around
    // sequence can feed a mode built around speed.
    const link = q.links[Math.floor(Math.random() * q.links.length)];
    if (!link) return null;
    const right = link.options[link.answer];
    const s = scramble([...link.options], link.answer);
    return { ...base, kind: 'choice', prompt: link.prompt, ...s, reason: link.reason, aus: isAusAnswer(q, right) };
  }

  // Sort It returns null on purpose. Dragging five tiles into order cannot be
  // done in four seconds, so including it would mean every Endurance run ended
  // on a Sort It question through no fault of the player.
  return null;
}

const QUICKFIRE_MODES = [
  'last-gasp',
  'higher-lower',
  'odd-one-out',
  'guess-the-thing',
  'chain-reaction',
];

/** A shuffled Endurance queue drawn from every mode that can be tapped. */
export function drawQuickfire({ topics, difficulty, count = 40 } = {}) {
  const perMode = Math.ceil(count / QUICKFIRE_MODES.length);
  const drawn = QUICKFIRE_MODES.flatMap((mode) =>
    drawQuestions(mode, { topics, difficulty, count: perMode }),
  );
  return shuffle(drawn).map(toQuickfire).filter(Boolean).slice(0, count);
}
